import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService, UserInfo } from '../../services/auth.service';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { MessageModule } from 'primeng/message';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    PasswordModule,
    MessageModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css']
})
export class ProfileComponent implements OnInit {
  currentUser: UserInfo | null = null;
  passwordForm: FormGroup;
  isChangingPassword = false;

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private messageService: MessageService
  ) {
    this.passwordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });
  }

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  }

  getRoleBadge(): string {
    if (!this.currentUser) return '';
    
    switch (this.currentUser.role) {
      case 'ADMIN':
        return 'Administrateur';
      case 'COMMERCIAL':
        return 'Commercial';
      case 'VIEWER':
        return 'Lecteur';
      default:
        return '';
    }
  }

  getRoleColor(): string {
    if (!this.currentUser) return '#6366F1';
    
    switch (this.currentUser.role) {
      case 'ADMIN':
        return '#EF4444';
      case 'COMMERCIAL':
        return '#10B981';
      case 'VIEWER':
        return '#6366F1';
      default:
        return '#6366F1';
    }
  }

  onChangePassword(): void {
    if (this.passwordForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Veuillez remplir tous les champs correctement'
      });
      return;
    }

    const currentPassword = this.passwordForm.get('currentPassword')?.value;
    const newPassword = this.passwordForm.get('newPassword')?.value;

    // Validation supplémentaire
    if (!currentPassword || !newPassword) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Tous les champs sont obligatoires'
      });
      return;
    }

    this.isChangingPassword = true;

    const request = {
      currentPassword: currentPassword.trim(),
      newPassword: newPassword.trim()
    };

    console.log('🔐 Sending password change request:', { 
      hasCurrentPassword: !!request.currentPassword,
      hasNewPassword: !!request.newPassword,
      currentPasswordLength: request.currentPassword.length,
      newPasswordLength: request.newPassword.length
    });

    this.authService.changePassword(request).subscribe({
      next: (response) => {
        this.isChangingPassword = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: response.message || 'Mot de passe modifié avec succès'
        });
        this.passwordForm.reset();
      },
      error: (error) => {
        this.isChangingPassword = false;
        const errorMessage = error.error?.message || error.error?.detail || 'Erreur lors du changement de mot de passe';
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: errorMessage
        });
      }
    });
  }

  getUserInitials(): string {
    if (!this.currentUser) return '';
    
    const firstInitial = this.currentUser.prenom?.charAt(0)?.toUpperCase() || '';
    const lastInitial = this.currentUser.nom?.charAt(0)?.toUpperCase() || '';
    
    return `${firstInitial}${lastInitial}`;
  }
}
